import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import ArticleIcon from "@mui/icons-material/Article";
import ShortTextIcon from "@mui/icons-material/ShortText";
import { Avatar, Box, Button, CircularProgress, Paper, Typography, Grid } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import CopyButtonOrIcon from "../../../components/common/CopyButtonOrIcon";
import { MarkdownContentBox } from "../../../components/common/MarkdownContentBox";
import ImageModal from "../../../components/common/ImageModal";
import { useCustomNavigate } from "../../../hooks/useCustomNavigate";
import { Images } from "../../../assets";
import { AnswerBoxProps } from "../../../types/chatTypes";
import { alpha } from "@mui/material/styles";

export default function AnswerBox({ historyItem, setSelectSourceId }: AnswerBoxProps) {
  const { t } = useTranslation();
  const navigate = useCustomNavigate();
  const [navigating, setNavigating] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const deletedImageIndices = useRef<Set<number>>(new Set());

  const handleImageClick = (index: number) => {
    // 削除済みとマークされた画像はモーダルを開かない
    if (!deletedImageIndices.current.has(index)) {
      setSelectedImageIndex(index);
    }
  };

  const handleModalClose = () => {
    setSelectedImageIndex(null);
  };

  const handleNavigate = (key: string) => {
    switch (key) {
      case "article":
        navigate("/writing-ai/article", { state: { text: historyItem.content } });
        break;
      case "mindMap":
        navigate("/mind-map", { state: { text: historyItem.content } });
        break;
      case "summary":
        navigate("/writing-ai/summarizer", { state: { text: historyItem.content } });
        break;
      default:
        break;
    }
  };

  const handleNewFile = () => {
    setNavigating(true);

    // レコードを作成せず、ステートでテキストを渡してナビゲーション
    navigate("/notes-nebula/files/new", {
      state: {
        content: historyItem.content,
        title: historyItem.content ? historyItem.content.split("\n")[0].slice(0, 36) : "Untitled",
      },
    });
  };

  return (
    <Paper
      data-id={historyItem.message_uuid}
      elevation={1}
      sx={{
        px: { xs: 2, sm: 3 },
        pt: 2,
        pb: 2,
        mb: 1,
        transition: "all 0.3s ease",
        backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.7),
        boxShadow: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "left",
          flexDirection: "row",
        }}
      >
        <Avatar alt="User avatar" src={Images.tenak.small} sx={{ width: 32, height: 32 }} />
        <Typography variant="subtitle1" component={"p"} sx={{ ml: 2 }} color={"textSecondary"}>
          {t("title")}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Box display="flex" justifyContent="right" alignItems={"flex-start"} gap={1}>
          <Tooltip title={t("browsing.save")} placement="top">
            <IconButton size="small" aria-label="article" onClick={() => handleNewFile()} disabled={navigating}>
              {navigating ? (
                <CircularProgress size={24} />
              ) : (
                <img src={Images.notesNebula.ico} alt="save" style={{ height: 24 }} />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title={t("browsing.article")} placement="top">
            <IconButton size="small" aria-label="article" onClick={() => handleNavigate("article")}>
              <ArticleIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("browsing.mindMap")} placement="top">
            <IconButton size="small" aria-label="article" onClick={() => handleNavigate("mindMap")}>
              <AccountTreeOutlinedIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t("browsing.summary")} placement="top">
            <IconButton size="small" aria-label="article" onClick={() => handleNavigate("summary")}>
              <ShortTextIcon />
            </IconButton>
          </Tooltip>
          <CopyButtonOrIcon displayType={"icon"} textToCopy={historyItem.content} size={"small"} />
        </Box>
      </Box>
      <Box flexDirection={"column"} justifyContent={"left"} display={"flex"} sx={{ overflowWrap: "break-word" }} mt={1}>
        {historyItem.content && historyItem.content.length > 0 && (
          <>
            <Box
              sx={{
                "& img": { maxWidth: "20% !important", height: "auto !important", borderRadius: 2, display: "none" },
              }}
            >
              <MarkdownContentBox>{historyItem.content}</MarkdownContentBox>
            </Box>
            {historyItem.search_results && (
              <Box sx={{ mt: 1 }}>
                <Button
                  variant="text"
                  color="primary"
                  onClick={() => setSelectSourceId(historyItem.message_uuid ?? "")}
                >
                  <Typography variant="button">SOURCE</Typography>
                </Button>
              </Box>
            )}
          </>
        )}

        {/* AIが生成した画像の表示 */}
        {historyItem.image_objects && historyItem.image_objects.length > 0 && (
          <>
            <Grid container spacing={1} sx={{ my: 1 }}>
              {historyItem.image_objects.map((imageObj, index) => (
                <Grid key={index} size={{ xs: 3, sm: 4 }}>
                  {imageObj.url && (
                    <Box
                      component="img"
                      src={imageObj.url}
                      alt={`Generated Image ${index + 1}`}
                      sx={{
                        width: "100%",
                        height: "auto",
                        borderRadius: 2,
                        cursor: "pointer",
                        transition: "opacity 0.2s",
                        "&:hover": {
                          opacity: 0.7,
                        },
                      }}
                      onClick={() => handleImageClick(index)}
                      onError={(e) => {
                        // 画像読み込みエラー時にURLを無効とみなす
                        const target = e.target as HTMLImageElement;
                        target.style.display = "none";
                        const parent = target.parentElement;
                        // 削除済みインデックスとして記録
                        deletedImageIndices.current.add(index);
                        if (parent) {
                          const textElement = document.createElement("div");
                          textElement.textContent = t("browsing.imageDeleted");
                          textElement.style.display = "flex";
                          textElement.style.alignItems = "center";
                          textElement.style.justifyContent = "center";
                          textElement.style.color = "#666";
                          textElement.style.fontSize = "0.8rem";
                          parent.appendChild(textElement);
                        }
                      }}
                    />
                  )}
                </Grid>
              ))}
            </Grid>
            {selectedImageIndex !== null &&
              historyItem.image_objects[selectedImageIndex] &&
              !deletedImageIndices.current.has(selectedImageIndex) && (
                <ImageModal
                  src={historyItem.image_objects[selectedImageIndex].url}
                  alt={`Generated Image ${selectedImageIndex + 1}`}
                  open={true}
                  onClose={handleModalClose}
                  generatedImageUuid={historyItem.image_objects[selectedImageIndex].generated_image_uuid}
                />
              )}
          </>
        )}

        {(!historyItem.content || historyItem.content.length === 0) &&
          (!historyItem.image_objects || historyItem.image_objects.length === 0) && ( // image_urls から image_objects に変更
            <Typography variant="body2" textAlign="left" color={"error"} sx={{ fontWeight: "bold", ml: 2, mt: 2 }}>
              No answer
            </Typography>
          )}
      </Box>
    </Paper>
  );
}
